import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Modal } from '@safetyhub/ui';
import { formatAnnexLabel, formatDateISO, type AdminLawAnnex } from '@safetyhub/shared';
import { adminApi, extractApiError } from '../../lib/api';

/** 조회 키 — `['admin','regulations']` 접두의 하위. 별표는 수집(03:00 cron · 「업데이트」)에만 바뀐다 */
export const LAW_ANNEXES_QUERY_KEY = (lawName: string) => ['admin', 'regulations', 'annexes', lawName] as const;

interface LawAnnexesModalProps {
  open: boolean;
  onClose: () => void;
  /** 고를 수 있는 법령명 — 법규 화면의 `getRegulationTypes` 결과(`laws.name_kr`) */
  lawNames: string[];
  /** 처음 고른 법령 — 화면의 「법규 유형」 필터값. 없으면 첫 이름 */
  initialLawName?: string;
  /**
   * 고르기 모드(W3 2026-11-04 · 카탈로그 조건 줄의 근거 선택) — 주면 본문 머리에 「이 별표를 근거로 지정」 버튼이 생기고 그 별표의 키
   * (법제처 법령ID · 번호 · 가지번호)를 돌려준다. ⚠️ 그래도 이 모달은 **아무것도 쓰지 않는다** — 저장은 부른 쪽(조건 PATCH)이 하고 서버가 그 별표의
   * 실재를 현행 버전에서 다시 본다. 법령ID가 없는 행(`law.lawId === null`)에서는 버튼이 막힌다(근거 키가 법령ID라 고를 수 없다)
   */
  onPick?: (pick: LawAnnexPick) => void;
  /** 모달 층 — 다른 모달 위에서 열 때 여는 쪽이 준다(선례 `OrganizationDetailModal`의 `z-[80]` · `bg-black/25`). 없으면 최상위 기본값 */
  overlayZClassName?: string;
  overlayBgClassName?: string;
}

/** 고르기 모드가 돌려주는 근거 키 — 카탈로그 조건 줄의 `LAW_ANNEX` 근거(`sourceLawId`·`sourceAnnexNumber`·`sourceAnnexSubNumber`) */
export interface LawAnnexPick {
  lawId: string;
  lawName: string;
  annexNumber: string;
  annexSubNumber: string;
  title: string;
}

/**
 * **법령 별표 열람** — S1(설계 `docs/plans/2026-11-01-law-annex-collection-design.md` §4-④ · 출시 조건 ①-c 9-m ⓐ).
 *
 * 운영자가 카탈로그 조건 행(선임 인원 기준)을 넣을 때 **별표 원문을 화면에서 읽는** 자리다. 별표는 **알림원이지
 * 입력원이 아니다**(사용자 결정 2026-10-27) — 이 모달은 아무것도 쓰지 않고, 기준값은 법정 의무 카탈로그에서 사람이 넣는다.
 *
 * 무엇을 싣는지(현행 = 부모 `laws.is_active` 조인 · 법령명 완전일치 · `countsByKind`)는 서버
 * `AdminRegulationsService.listAnnexes`가 정한다. 여기서는 **「별표」만** 요청한다(Q1 — 서식은 저장하되 이 화면에 안 보인다 ·
 * 그 수는 `countsByKind`로 「서식 N건은 보이지 않습니다」라고 말한다).
 *
 * 원문은 고정폭 `<pre>`(`whitespace-pre`)로 그린다 — 괘선 표(`┌─┬─┐`)는 줄바꿈·칸 공백이 곧 구조라 접으면 읽을 수 없다.
 * 세 문장을 가른다: 「현행 행 없음」(`law: null` — 수집 대상 밖·현행 후보 없음) · 「수집된 별표 없음」(옛 버전 — 백필 전) ·
 * 「별표 N건」.
 */
export default function LawAnnexesModal({
  open,
  onClose,
  lawNames,
  initialLawName,
  onPick,
  overlayZClassName,
  overlayBgClassName,
}: LawAnnexesModalProps) {
  const [lawName, setLawName] = useState(initialLawName || lawNames[0] || '');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // 열릴 때마다 화면 필터값으로 되돌린다 — 닫힌 사이 필터가 바뀌어도 모달이 옛 선택을 들지 않게
  useEffect(() => {
    if (open) {
      setLawName(initialLawName || lawNames[0] || '');
      setSelectedId(null);
    }
  }, [open, initialLawName, lawNames]);

  const { data, isLoading, error } = useQuery({
    queryKey: LAW_ANNEXES_QUERY_KEY(lawName),
    queryFn: async () => {
      const res = await adminApi.getRegulationAnnexes({ lawName, kind: '별표' });
      if (!res.data) throw new Error('서버 응답에 본문이 없습니다. 잠시 후 다시 시도해 주세요.');
      return res.data;
    },
    enabled: open && lawName.length > 0,
  });

  const annexes = data?.annexes ?? [];
  const selected: AdminLawAnnex | null = useMemo(() => {
    if (annexes.length === 0) return null;
    return annexes.find((a) => a.id === selectedId) ?? annexes[0];
  }, [annexes, selectedId]);
  const hiddenCount = data
    ? Object.entries(data.countsByKind)
        .filter(([kind]) => kind !== '별표')
        .reduce((sum, [, n]) => sum + n, 0)
    : 0;

  if (!open) return null;

  return (
    <Modal
      open
      onClose={onClose}
      title={onPick ? '법령 별표 — 근거로 지정할 별표 고르기' : '법령 별표'}
      size="xl"
      overlayZClassName={overlayZClassName}
      overlayBgClassName={overlayBgClassName}
      footer={
        <Button variant="secondary" onClick={onClose}>
          닫기
        </Button>
      }
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="w-72">
            <label htmlFor="law-annexes-law" className="block text-sm font-medium text-gray-700 mb-1">
              법령
            </label>
            <select
              id="law-annexes-law"
              value={lawName}
              onChange={(e) => {
                setLawName(e.target.value);
                setSelectedId(null);
              }}
              className="w-full px-3 py-2 border rounded-control"
            >
              {lawNames.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>
          {data?.law && (
            <p className="text-sm text-gray-600">
              {`현행 시행본 · 시행 ${
                data.law.enforcementDate ? formatDateISO(new Date(data.law.enforcementDate)) : '-'
              } · 별표 ${annexes.length}건`}
              {hiddenCount > 0 && (
                <span className="ml-2 text-gray-400">{`서식 등 ${hiddenCount}건은 여기 보이지 않습니다`}</span>
              )}
            </p>
          )}
        </div>
        <p className="text-xs text-gray-500">
          별표는 알림원입니다 — 선임 인원 같은 기준값은 법정 의무 카탈로그의 조건 행에 입력하고, 이 원문은 그 대조용으로 읽습니다.
        </p>

        {isLoading ? (
          <div className="py-6 text-sm text-gray-500">불러오는 중...</div>
        ) : error ? (
          <div className="p-3 bg-red-50 border border-red-200 rounded-card text-sm text-red-700">
            별표를 불러오지 못했습니다. {extractApiError(error).message}
          </div>
        ) : !data || data.law === null ? (
          <div className="py-6 text-sm text-gray-500">
            이 법령의 현행 시행본을 찾지 못했습니다 — 수집 대상이 아니거나 현행 후보가 없는 그룹입니다.
          </div>
        ) : annexes.length === 0 ? (
          <div className="py-6 text-sm text-gray-500">
            이 시행본에 수집된 별표가 없습니다. 2026-11-01 이전에 수집된 시행본은 별표 백필이 돌기 전까지 비어 있습니다.
          </div>
        ) : (
          <div className="grid grid-cols-[16rem_1fr] gap-4 min-h-[24rem]">
            <ul aria-label="별표 목록" className="divide-y divide-gray-100 border rounded-card overflow-y-auto max-h-[60vh]">
              {annexes.map((a) => {
                const isSelected = selected?.id === a.id;
                return (
                  <li key={a.id}>
                    <button
                      type="button"
                      aria-pressed={isSelected}
                      onClick={() => setSelectedId(a.id)}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                        isSelected ? 'bg-brand-50 text-brand-700 font-medium' : 'text-gray-800'
                      }`}
                    >
                      <span className="block">{formatAnnexLabel(a.annexKind, a.annexNumber, a.annexSubNumber)}</span>
                      <span className="block text-xs text-gray-500 truncate">{a.title}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
            {selected && (
              <section aria-label={formatAnnexLabel(selected.annexKind, selected.annexNumber, selected.annexSubNumber)}>
                <h3 className="text-sm font-bold text-gray-900 mb-2">
                  {`${formatAnnexLabel(selected.annexKind, selected.annexNumber, selected.annexSubNumber)} — ${selected.title}`}
                </h3>
                {onPick && (
                  <Button
                    size="sm"
                    className="mb-2"
                    disabled={!data?.law?.lawId}
                    title={data?.law?.lawId ? undefined : '이 법령 행에 법제처 법령ID가 없어 근거로 지정할 수 없습니다.'}
                    onClick={() => {
                      const lawId = data?.law?.lawId;
                      if (!lawId || !data?.law) return;
                      onPick({
                        lawId,
                        lawName: data.law.nameKr,
                        annexNumber: selected.annexNumber,
                        annexSubNumber: selected.annexSubNumber,
                        title: selected.title,
                      });
                    }}
                  >
                    이 별표를 근거로 지정
                  </Button>
                )}
                {/* 괘선 표 — 줄바꿈·칸 공백이 구조라 whitespace-pre + 고정폭. 접으면(pre-wrap) 열이 어긋난다 */}
                <pre className="p-3 bg-gray-50 rounded-card text-xs font-mono whitespace-pre overflow-auto max-h-[60vh]">
                  {selected.content}
                </pre>
              </section>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
